/**
 * CityNightLightsModule — every settlement's lights at night, near to far (the night-sky realism wave, 2026-10-06;
 * agent L). "More light coming from cities, especially at night from a distance": before this a far town at night was
 * its far tier's boxes glowing a uniform blue-grey.
 *
 *   THE CARPET — one instanced additive draw of constant-screen-size points: the street heads along each town's streets
 *     and the lit-window clusters on its buildings, from the baked map file (lib/city_night_lights.ts places them; the
 *     shader's photometry is lib/city_lights_material.ts). Every settlement within the light-pollution model's 60 km
 *     (SOURCE_MAX_KM) at a level of detail by distance (full < 8 km; 40 / 100 / 250 m cells beyond — flux conserved),
 *     inside a points budget by tier (the farthest coarsen first). Colours and strengths agree with the town's dome
 *     (the skyglowLive() source tints; the same model the sky draws). A BUILT town keeps its real lamps and windows near
 *     the camera: the carpet fades in only past them (the near fade), so nothing doubles.
 *   THE HAZE — the town's own low glow: the K light domes extended below the horizon onto the ground and buildings
 *     around a lit town (zero where the sky shows — the sky draws the dome there).
 *   THE PLAYER CITIES (agent P, 2026-10-06) — a player-founded city is runtime rows, never baked: its carpet is built
 *     from its `structure` rows (lib/city_night_lights `playerCityLightPoints`: each deed's footprint through the baked
 *     building path, a head on every street lamp, floodlit faces on the hall and the monuments) and joins the same
 *     budget, levels and photometry. Re-read only when the `city` / `structure` / `city_allegiance` stamps move
 *     (lib/player_city_lights); its lamp heads are registered with StreetLampsModule as `pcity:<cityId>` (the near head
 *     glows and pooled lights); an invalid city is dark.
 *
 * Off by day (the photocells follow the sky's night factor), off outside the Commons and in space. RenderSub/11 (after
 * the skyglow at 3, the sky at 5–7, the street lamps at 8). The map file is fetched lazily the first night
 * (map_file_load — shared with the maps). Diag `__cityLights()`; opt out `localStorage atlas:city-lights = 'off'`
 * (`haze-off` keeps the points, drops the haze).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { uNightFactor, currentTod } from './SkyModule.js';
import { activePreset, activeTier, currentDrawDistance, distantDetailOn } from '../lib/quality_tiers.js';
import { SETTLEMENT_FOOTPRINT, type SettlementTier } from '../../../shared/settlements.js';
import { stampedHeight } from '../lib/terrain_stamp.js';
import { civilizationOf } from '../lib/biome_settlements.js';
import { loadMapFile, loadedMapFile } from '../lib/map_file_load.js';
import type { MapFile, MapSite } from '../lib/map_structures.js';
import { emissiveNits, exposureState, lightFrame } from '../lib/photometry.js';
import { naturalSkyLift, naturalDisplayGainNow } from '../lib/sky/night_sky_model.js';
import { SOURCE_MAX_KM, NATURAL_SQM, sqmToNits, tintOfCiv, type LightSource } from '../lib/light_pollution.js';
import { skyglowLive } from '../lib/light_pollution_live.js';
import {
  siteLightPoints, siteLightLevels, chooseLevels, litFractions, hourOfTod, lightsOn, isWindowClass,
  POINT_BYTES, LIGHT_CLASS, KNEE_EV, realLampBytes, playerCityLightPoints, type LightPoints,
} from '../lib/city_night_lights.js';
import { gatherPlayerCities, playerCitiesStamp, ensurePlayerCitySubs, type PlayerCityLive, type PlayerCityDb } from '../lib/player_city_lights.js';
import { warmThenShow } from '../lib/pipeline_warm.js';
import { litLampsOf, lampRegistryVersion, registerLamps, unregisterLamps, type LampAnchor } from './StreetLampsModule.js';
import { createCityLightsMaterial, createCityLightsGeometry, createCityHazeMaterial, HAZE_DOMES, type CityLightUniforms, type CityHazeUniforms } from '../lib/city_lights_material.js';

/** Points the carpet may hold, by tier (20 B a point: 32 k = 640 KB). */
export const CITY_LIGHTS_BUDGET: Record<string, number> = { minimal: 8000, potato: 16000, low: 24000 };
const BUDGET_DEFAULT = 32000;
const CAPACITY = 32768;
/** The carpet's peak cap, stops over the scene key (photometry: the contract's 10 keeps a point white-hot; 9 keeps its hue). */
export const CITY_LIGHTS_CAP_EV = 9;
/**
 * Inside these distances of the camera a BUILT town's own lamps and windows carry the near field (the carpet fades in
 * over 1.6× the distance). Street heads: StreetLamps' head glows reach 220 m, and a carpet dot nearer than ~150 m would
 * float with no pole under it. Windows: with interior mapping (med+) the real glass shows lit rooms, so the carpet's
 * clusters wait until a facade is a few pixels; without it the town's windows would be dark, so they come in early.
 */
export const BUILT_LAMP_FADE_M = 300;
/** A REAL head's carpet point fades in past this (inside it the head glow sprite and the pooled point lights carry it). */
export const REAL_HEAD_FADE_M = 30;
export const BUILT_WINDOW_FADE_M = 600;
export const BUILT_WINDOW_FADE_FLAT_M = 220;
/**
 * A PLAYER CITY's windows and floods fade in past this (agent P, 2026-10-06). Its buildings are deeds, drawn by
 * StructureRenderModule everywhere in the world, so a player city is always "built" — and the deed set's glass is NOT
 * interior-mapped (each set GLB is one plain `Material_0` with no emissive and no glass name — measured on the 27 set
 * models), so up close its windows are dark: L's rule for a built town whose glass is not interior-mapped
 * (BUILT_WINDOW_FADE_FLAT_M), on every tier. Its street lamps are real heads: REAL_HEAD_FADE_M, with StreetLampsModule's
 * head glows and pooled lights inside it (the lamps are registered there as `pcity:<cityId>`).
 */
export const PLAYER_WINDOW_FADE_M = BUILT_WINDOW_FADE_FLAT_M;
/** Re-read the player cities at most this often when a table stamp moved, or on this slower clock when a table is unpatched (ms). */
const PLAYER_GATHER_MS = 1000;
const PLAYER_GATHER_UNPATCHED_MS = 5000;
/** Recompose at most this often (ms), and generate site sets at most this long per frame (ms). */
const RECOMPOSE_MS = 600;
const GEN_BUDGET_MS = 6;
/** Point sets of sites this far from the camera are dropped (regenerated if it comes back: ~1 ms a city). */
const EVICT_M = 90_000;

/** Does the carpet carry the far towns at night (so the far tier's boxes stand dark)? False when opted out. */
export function cityLightsCarryFarTier(): boolean {
  try { return localStorage.getItem('atlas:city-lights') !== 'off'; } catch { return true; }
}

interface SiteLike { id: number; centerX: number; centerZ: number; tier: SettlementTier; siteSeed: number; capitalId?: number }
interface SiteEntry { site: SiteLike; map: MapSite; levels: LightPoints[] | null; groundY: number; counts: number[]; tint?: [number, number, number] }
/** A player city's carpet: its level sets (regenerated when its signature changes), its centre ground, its lamp heads. */
interface PlayerEntry { live: PlayerCityLive; levels: LightPoints[]; counts: number[]; groundY: number; lamps: LampAnchor[]; version: number }
type Cand = { dist: number; built: boolean; counts: readonly number[]; e?: SiteEntry; p?: PlayerEntry };

export class CityNightLightsModule implements IModule {
  readonly name = 'CityNightLights';
  private app!: App;
  private group: THREE.Group | null = null;
  private mesh: THREE.Mesh | null = null;
  private attrs: ReturnType<typeof createCityLightsGeometry> | null = null;
  private u: CityLightUniforms | null = null;
  private haze: THREE.Mesh | null = null;
  private hazeGroup: THREE.Group | null = null;
  private hu: CityHazeUniforms | null = null;
  private seed = '';
  private entries = new Map<number, SiteEntry>();
  private genQueue: number[] = [];
  private lastCompose = 0;
  private composeKey = '';
  private originX = 0;
  private originZ = 0;
  /** The batch and the haze compile off the frame once (lib/pipeline_warm): the first dusk must not hitch. */
  private warmed = false;
  private hazeWarmed = false;
  private readonly camW = new THREE.Vector3();
  /** The player cities (agent P): `p<cityId>` → its carpet, re-read only when a table stamp moves. */
  private players = new Map<string, PlayerEntry>();
  private playerStamp = '';
  private playerAt = 0;
  private playerVersion = 0;
  /** The lamp registry's version after our last registration — a world hop's flush is noticed by its change. */
  private lampSeen = -1;
  readonly stats = { points: 0, sites: 0, levels: [0, 0, 0, 0], generated: 0, genMs: 0, composes: 0, budget: BUDGET_DEFAULT, kb: 0, on: 0, built: 0, realHeads: 0, mapSites: 0, hazeDomes: 0, lastComposeMs: 0,
    playerCities: 0, playerStructures: 0, playerPoints: 0, playerLamps: 0, playerDrawn: 0, playerGathers: 0, playerBuilds: 0 };

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('CityNightLights', Phase.RenderSub, 11, () => this.tick()));
    const g = globalThis as Record<string, unknown>;
    g.__cityLights = () => ({ ...this.stats, levels: [...this.stats.levels], visible: !!this.mesh?.visible, haze: !!this.haze?.visible, origin: [this.originX, this.originZ], optOut: CityNightLightsModule.opt(),
      players: [...this.players.values()].map((p) => ({ id: p.live.key, name: p.live.name, rank: p.live.rank, status: p.live.status, civ: p.live.alignment, structures: p.live.structures.length, points: p.counts, lamps: p.lamps.length, registered: litLampsOf(`pcity:${p.live.cityId}`).length })),
      uniforms: this.u ? { nitsScale: this.u.nitsScale.value, kneeScene: this.u.kneeScene.value, capScene: this.u.capScene.value, fogDensity: this.u.fogDensity.value, tanHalfFov: this.u.tanHalfFov.value, lit: this.u.lit.value.toArray(), lampsOn: this.u.lampsOn.value } : null,
      exposure: { evScene: exposureState.evScene, ev100: exposureState.ev100, luxToScene: exposureState.luxToScene, exposure: exposureState.exposure } });
  }

  private static opt(): string { try { return localStorage.getItem('atlas:city-lights') ?? ''; } catch { return ''; } }

  private commonsSeed(): string | null {
    const conn = atlas.conn;
    if (!conn) return null;
    for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
    return null;
  }

  private ensureMeshes(): void {
    if (this.mesh) return;
    const scene = this.app.world.scene;
    this.attrs = createCityLightsGeometry(CAPACITY);
    const { material, uniforms } = createCityLightsMaterial();
    this.u = uniforms;
    const mesh = new THREE.Mesh(this.attrs.geometry, material);
    mesh.name = 'city-night-lights';
    mesh.frustumCulled = false;
    mesh.castShadow = false; mesh.receiveShadow = false;
    mesh.renderOrder = 2;
    this.group = new THREE.Group();
    this.group.name = 'city-night-lights-origin';
    this.group.add(mesh);
    scene.add(this.group);
    this.mesh = mesh;
    const hz = createCityHazeMaterial();
    this.hu = hz.uniforms;
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), hz.material);
    quad.name = 'city-night-haze';
    quad.frustumCulled = false;
    quad.renderOrder = 3;
    quad.visible = false;
    this.hazeGroup = new THREE.Group();
    this.hazeGroup.name = 'city-night-haze-group';
    this.hazeGroup.add(quad);
    scene.add(this.hazeGroup);
    this.haze = quad;
  }

  private hide(): void {
    if (this.mesh) this.mesh.visible = false;
    if (this.haze) this.haze.visible = false;
  }

  private tick(): void {
    const opt = CityNightLightsModule.opt();
    const inCommons = atlas.currentWorldId != null && atlas.currentWorldId === atlas.commonsWorldId && !atlas.spaceFlight;
    const night = (uNightFactor as unknown as { value: number }).value ?? 0;
    const on = lightsOn(night);
    this.stats.on = on;
    // The player cities (agent P): their sets and lamp heads follow the rows by day too, so the lamps are registered
    // with StreetLampsModule before its dusk. A stamp compare a frame — the tables are read only when one moved.
    if (inCommons) this.refreshPlayers();
    if (opt === 'off' || !inCommons || on <= 0) { this.hide(); return; }
    const seed = this.commonsSeed();
    if (!seed) { this.hide(); return; }
    if (seed !== this.seed) { this.seed = seed; this.entries.clear(); this.genQueue = []; this.composeKey = ''; void loadMapFile(seed); }
    const file = loadedMapFile(seed);
    if (!file) { this.hide(); return; }
    this.ensureMeshes();
    const camera = this.app.renderer.cameras.main as THREE.PerspectiveCamera | undefined;
    if (!camera) return;
    camera.getWorldPosition(this.camW);
    const sp = this.app.world.scene.position;
    const cx = this.camW.x - sp.x, cy = this.camW.y - sp.y, cz = this.camW.z - sp.z; // ABSOLUTE camera position
    const now = performance.now();
    this.generate(file);
    if (now - this.lastCompose > RECOMPOSE_MS) { this.lastCompose = now; this.compose(cx, cz); }
    // ── per-frame uniforms (THE PHYSICAL-EMISSIVE CONTRACT: the emissive scale moves with the hour and the metering) ──
    const u = this.u!;
    u.cam.value.set(cx - this.originX, cy, cz - this.originZ);
    u.tanHalfFov.value = Math.tan(((camera.fov ?? 60) * Math.PI) / 360) / Math.max(0.01, camera.zoom ?? 1);
    const fog = this.app.world.scene.fog as (THREE.FogExp2 & { isFogExp2?: boolean }) | (THREE.Fog & { isFog?: boolean }) | null;
    u.fogDensity.value = fog && (fog as THREE.FogExp2).isFogExp2 ? (fog as THREE.FogExp2).density : fog && (fog as THREE.Fog).isFog ? 1.978 / Math.max(1, (fog as THREE.Fog).far) : 0;
    u.nitsScale.value = emissiveNits(1);
    u.kneeScene.value = emissiveNits(1e12, KNEE_EV);
    u.capScene.value = emissiveNits(1e12, CITY_LIGHTS_CAP_EV);
    const lit = litFractions(hourOfTod(currentTod()));
    u.lit.value.set(lit[0], lit[1], lit[2], lit[3]);
    u.lampsOn.value = on;
    u.winOn.value = on;
    u.clock.value = (now / 1000) % 1000;
    if (this.mesh) this.mesh.visible = this.stats.points > 0;
    if (!this.warmed && this.stats.points > 0 && this.group) { this.warmed = true; void warmThenShow(this.app, this.group); }
    this.tickHaze(on, opt === 'haze-off' || activeTier() === 'minimal');
  }

  /** Generate site point sets a few milliseconds per frame (a city ~1 ms; the first night ~100 ms in all). */
  private generate(file: MapFile): void {
    const settle = this.app.getModule?.('CommonsSettlements') as unknown as { sites?: SiteLike[] | null } | undefined;
    const sites = settle?.sites;
    if (!sites) return;
    if (this.entries.size === 0 && this.genQueue.length === 0) {
      const byId = new Map(file.sites.map((s) => [s.id, s]));
      for (const s of sites) {
        const map = byId.get(s.id);
        if (!map) continue;
        this.entries.set(s.id, { site: s, map, levels: null, groundY: 0, counts: [0, 0, 0, 0] });
      }
      this.stats.mapSites = this.entries.size;
    }
    if (!this.genQueue.length) return;
    const t0 = performance.now();
    const sources = skyglowLive().sources;
    while (this.genQueue.length && performance.now() - t0 < GEN_BUDGET_MS) {
      const id = this.genQueue.shift()!;
      const e = this.entries.get(id);
      if (!e || e.levels) continue;
      const src: LightSource | undefined = sources.find((q) => q.id === String(e.site.capitalId ?? e.site.id)) ?? sources.find((q) => q.id === String(e.site.id));
      const civ = civilizationOf(e.site.siteSeed) as 'a' | 'b';
      e.tint = src?.tint;
      const pts = siteLightPoints(e.map, file, { tier: e.site.tier, civ, tint: src?.tint });
      e.levels = siteLightLevels(pts);
      e.counts = e.levels.map((l) => l.n);
      e.groundY = stampedHeight(this.seed, e.site.centerX, e.site.centerZ);
      this.stats.generated++;
    }
    this.stats.genMs += performance.now() - t0;
  }

  /**
   * The player cities (agent P, 2026-10-06): re-read the `city` / `structure` / `city_allegiance` rows when their stamps
   * move (lib/player_city_lights — never a per-frame walk), regenerate a city's points only when its signature changed,
   * drop the cities that went dark (invalid, disbanded), and keep their lamp heads registered with StreetLampsModule
   * (`pcity:<cityId>`; a world hop's flush is re-registered when the registry version moves).
   */
  private refreshPlayers(): void {
    const conn = atlas.conn as unknown as { db?: PlayerCityDb } | null;
    if (!conn?.db) return;
    ensurePlayerCitySubs(conn);
    const now = performance.now();
    const stamp = playerCitiesStamp(conn.db);
    const unpatched = stamp.includes('NaN');
    const due = unpatched ? now - this.playerAt >= PLAYER_GATHER_UNPATCHED_MS : stamp !== this.playerStamp && now - this.playerAt >= PLAYER_GATHER_MS;
    if (due) {
      const seed = this.commonsSeed();
      if (seed) {
        this.playerStamp = stamp; this.playerAt = now; this.stats.playerGathers++;
        const live = gatherPlayerCities(conn.db, atlas.commonsWorldId);
        const keep = new Set<string>();
        for (const c of live) {
          keep.add(c.key);
          const had = this.players.get(c.key);
          if (had && had.live.sig === c.sig) { had.live = c; continue; }
          const res = playerCityLightPoints({ cityId: c.cityId, centerX: c.centerX, centerZ: c.centerZ, civ: c.civ, tint: tintOfCiv(c.civ), structures: c.structures, groundAt: (x, z) => stampedHeight(seed, x, z) });
          const levels = siteLightLevels(res.points);
          this.players.set(c.key, { live: c, levels, counts: levels.map((l) => l.n), groundY: res.groundY, lamps: res.lamps, version: ++this.playerVersion });
          this.stats.playerBuilds++;
          if (res.lamps.length) registerLamps(`pcity:${c.cityId}`, res.lamps); else unregisterLamps(`pcity:${c.cityId}`);
        }
        for (const [k, p] of this.players) if (!keep.has(k)) { unregisterLamps(`pcity:${p.live.cityId}`); this.players.delete(k); } // a city gone dark takes its lamps with it
        this.lampSeen = lampRegistryVersion();
        let structures = 0, points = 0, lamps = 0;
        for (const p of this.players.values()) { structures += p.live.structures.length; points += p.counts[0]; lamps += p.lamps.length; }
        this.stats.playerCities = this.players.size; this.stats.playerStructures = structures; this.stats.playerPoints = points; this.stats.playerLamps = lamps;
      }
    }
    // The registry was flushed (`atlas:world-cleared`) or rebuilt behind us: put our heads back if they are missing.
    if (lampRegistryVersion() !== this.lampSeen) {
      for (const p of this.players.values()) if (p.lamps.length && litLampsOf(`pcity:${p.live.cityId}`).length !== p.lamps.length) registerLamps(`pcity:${p.live.cityId}`, p.lamps);
      this.lampSeen = lampRegistryVersion();
    }
  }

  /** Choose the sites and levels, and rewrite the batch when the choice changed. */
  private compose(cx: number, cz: number): void {
    const t0 = performance.now();
    const maxM = distantDetailOn() ? SOURCE_MAX_KM * 1000 : currentDrawDistance();
    const settle = this.app.getModule?.('CommonsSettlements') as unknown as { isBuilt?: (id: number) => boolean } | undefined;
    const cand: Cand[] = [];
    for (const e of this.entries.values()) {
      const r = SETTLEMENT_FOOTPRINT[e.site.tier] ?? 200;
      const dist = Math.max(0, Math.hypot(e.site.centerX - cx, e.site.centerZ - cz) - r);
      if (dist > maxM) { if (e.levels && dist > EVICT_M) { e.levels = null; e.counts = [0, 0, 0, 0]; } continue; } // a far site's sets are dropped (~40 KB a town)
      if (!e.levels) { if (!this.genQueue.includes(e.site.id)) this.genQueue.push(e.site.id); continue; }
      cand.push({ e, dist, built: !!settle?.isBuilt?.(e.site.id), counts: e.counts });
    }
    // The player cities join the same budget and level choice: always "built" (their deeds stand), measured from their border.
    for (const p of this.players.values()) {
      const dist = Math.max(0, Math.hypot(p.live.centerX - cx, p.live.centerZ - cz) - p.live.radiusM);
      if (dist > maxM || p.counts[0] === 0) continue;
      cand.push({ p, dist, built: true, counts: p.counts });
    }
    // nearest first into the generator
    this.genQueue.sort((a, b) => {
      const ea = this.entries.get(a)!, eb = this.entries.get(b)!;
      return Math.hypot(ea.site.centerX - cx, ea.site.centerZ - cz) - Math.hypot(eb.site.centerX - cx, eb.site.centerZ - cz);
    });
    const budget = Math.min(CAPACITY, CITY_LIGHTS_BUDGET[activeTier()] ?? BUDGET_DEFAULT);
    this.stats.budget = budget;
    let heads = 0; for (const c of cand) if (c.built && c.e) heads += litLampsOf(`settle:${c.e.site.id}`).length; // the real heads share the budget
    const lv = chooseLevels(cand.map((c) => ({ dist: c.dist, counts: c.counts })), Math.max(2000, budget - heads));
    // The batch origin moves only when the camera strays 20 km (float32 keeps a millimetre at 60 km either way).
    if (Math.hypot(cx - this.originX, cz - this.originZ) > 20_000 || this.composeKey === '') { this.originX = Math.round(cx / 1000) * 1000; this.originZ = Math.round(cz / 1000) * 1000; }
    const key = `${this.originX},${this.originZ},${activePreset().interiorMapping ? 1 : 0},${lampRegistryVersion()}|` + cand.map((c, i) => (c.e ? `${c.e.site.id}:${lv[i]}:${c.built ? 1 : 0}` : `${c.p!.live.key}:${lv[i]}:${c.p!.version}`)).join(',');
    if (key === this.composeKey) return;
    this.composeKey = key;
    const A = this.attrs!;
    const pos = A.pos.array as Float32Array, col = A.col.array as Uint8Array, info = A.info.array as Uint8Array;
    let n = 0, built = 0, playerDrawn = 0;
    this.stats.realHeads = 0;
    const winFade = activePreset().interiorMapping ? BUILT_WINDOW_FADE_M : BUILT_WINDOW_FADE_FLAT_M;
    const levelsCount = [0, 0, 0, 0];
    for (let ci = 0; ci < cand.length; ci++) {
      const c = cand[ci], L = lv[ci];
      if (c.p) {
        // A PLAYER CITY: its windows and floods fade like a built town's dark-glass windows, its lamps like real heads.
        const pc = c.p, ps = pc.levels[L];
        levelsCount[L]++; playerDrawn++;
        const pox = pc.live.centerX - this.originX, poz = pc.live.centerZ - this.originZ, pgy = pc.groundY;
        for (let i = 0; i < ps.n && n < CAPACITY; i++, n++) {
          pos[n * 3] = pox + ps.xyz[i * 3];
          pos[n * 3 + 1] = pgy + ps.xyz[i * 3 + 1];
          pos[n * 3 + 2] = poz + ps.xyz[i * 3 + 2];
          const o = i * POINT_BYTES;
          col[n * 4] = ps.bytes[o]; col[n * 4 + 1] = ps.bytes[o + 1]; col[n * 4 + 2] = ps.bytes[o + 2]; col[n * 4 + 3] = ps.bytes[o + 3];
          const cls = ps.bytes[o + 4];
          info[n * 4] = cls; info[n * 4 + 1] = ps.bytes[o + 5]; info[n * 4 + 2] = ps.bytes[o + 6];
          const nearM = isWindowClass(cls) || cls === LIGHT_CLASS.flood ? PLAYER_WINDOW_FADE_M : REAL_HEAD_FADE_M;
          info[n * 4 + 3] = Math.min(255, Math.round(nearM / 16));
        }
        continue;
      }
      const e = c.e!;
      const set = e.levels![L];
      levelsCount[L]++;
      if (c.built) built++;
      const ox = e.site.centerX - this.originX, oz = e.site.centerZ - this.originZ, gy = e.groundY;
      for (let i = 0; i < set.n && n < CAPACITY; i++, n++) {
        pos[n * 3] = ox + set.xyz[i * 3];
        pos[n * 3 + 1] = gy + set.xyz[i * 3 + 1];
        pos[n * 3 + 2] = oz + set.xyz[i * 3 + 2];
        const o = i * POINT_BYTES;
        col[n * 4] = set.bytes[o]; col[n * 4 + 1] = set.bytes[o + 1]; col[n * 4 + 2] = set.bytes[o + 2]; col[n * 4 + 3] = set.bytes[o + 3];
        const cls = set.bytes[o + 4];
        info[n * 4] = cls; info[n * 4 + 1] = set.bytes[o + 5]; info[n * 4 + 2] = set.bytes[o + 6];
        // THE NEAR FADE: a built town's own lamps and windows carry the near field
        const nearM = !c.built ? 0 : (isWindowClass(cls) || cls === LIGHT_CLASS.flood ? winFade : BUILT_LAMP_FADE_M);
        info[n * 4 + 3] = Math.min(255, Math.round(nearM / 16));
      }
      // A BUILT town's real heads (the cooked lamp props carry no emissive): a point on every lit registered anchor,
      // exactly where the pole stands, so the town's streets keep their lights past the 64 nearest head glows.
      if (c.built) {
        const heads = litLampsOf(`settle:${e.site.id}`);
        const civ = civilizationOf(e.site.siteSeed) as 'a' | 'b';
        const tint = e.tint;
        for (let i = 0; i < heads.length && n < CAPACITY; i++, n++) {
          const h = heads[i];
          pos[n * 3] = h.x - this.originX; pos[n * 3 + 1] = h.y; pos[n * 3 + 2] = h.z - this.originZ;
          const b = realLampBytes(e.site.id, i, civ, tint);
          col[n * 4] = b[0]; col[n * 4 + 1] = b[1]; col[n * 4 + 2] = b[2]; col[n * 4 + 3] = b[3];
          info[n * 4] = b[4]; info[n * 4 + 1] = b[5]; info[n * 4 + 2] = b[6]; info[n * 4 + 3] = Math.round(REAL_HEAD_FADE_M / 16);
          this.stats.realHeads++;
        }
      }
    }
    A.pos.needsUpdate = true; A.col.needsUpdate = true; A.info.needsUpdate = true;
    A.geometry.instanceCount = n;
    this.group!.position.set(this.originX, 0, this.originZ);
    this.group!.updateMatrixWorld(true);
    this.stats.points = n; this.stats.sites = cand.length; this.stats.levels = levelsCount; this.stats.built = built; this.stats.playerDrawn = playerDrawn;
    this.stats.composes++; this.stats.kb = Math.round((n * 20) / 1024);
    this.stats.lastComposeMs = +(performance.now() - t0).toFixed(2);
  }

  /** The haze: the K domes over the camera (skyglowLive — the sky's own numbers), extended below the horizon. */
  private tickHaze(on: number, off: boolean): void {
    const hz = this.haze, hu = this.hu;
    if (!hz || !hu) return;
    const live = skyglowLive();
    if (off || !live.active || !live.domes.length) { hz.visible = false; this.stats.hazeDomes = 0; return; }
    let active = 0;
    for (let k = 0; k < HAZE_DOMES; k++) {
      const d = live.domes[k];
      if (!d) { hu.domeB[k].value.w = 0; continue; }
      const src = live.sources.find((q) => q.id === d.id);
      const glowR = Math.max(300, (src?.r ?? 0.5) * 1000 * 1.2);
      hu.domeA[k].value.set(d.dirX, d.dirZ, d.peak, d.halfWidth);
      hu.domeB[k].value.set(d.height, d.dist * 1000, glowR, 1);
      hu.tint[k].value.set(d.tint[0], d.tint[1], d.tint[2]);
      if (d.dist * 1000 > glowR) active++;
    }
    hu.unitScene.value = sqmToNits(NATURAL_SQM) * emissiveNits(1) * on;
    // agree with the sky's display of the domes (agent S's adapted artificial sky × the night-floor lift)
    hu.glowHorizon.value = live.glow.ratio * 2.2;
    // × THE NIGHT DISPLAY GAIN (2026-10-06): the sky draws every display term × the gain — the haze extends its domes.
    hu.lift.value = naturalSkyLift(Math.max(0, lightFrame.moon?.model.globalLux ?? 0)) * naturalDisplayGainNow();
    this.stats.hazeDomes = active;
    hz.visible = active > 0;
    if (!this.hazeWarmed && hz.visible && this.hazeGroup) { this.hazeWarmed = true; void warmThenShow(this.app, this.hazeGroup); }
  }

  dispose(): void {
    if (this.group) { this.group.parent?.remove(this.group); }
    if (this.mesh) { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh = null; }
    if (this.hazeGroup) this.hazeGroup.parent?.remove(this.hazeGroup);
    if (this.haze) { this.haze.geometry.dispose(); (this.haze.material as THREE.Material).dispose(); this.haze = null; }
    this.entries.clear();
    for (const p of this.players.values()) unregisterLamps(`pcity:${p.live.cityId}`);
    this.players.clear();
  }
}
